import styled from "styled-components";
import { TablePageCard, TablePageContainer, TablePageTitle } from "components/TablePageLayout";
import { pxToRem } from "styles/utils";

export const Page = styled(TablePageContainer)`
  min-width: 0;
`;

export const Content = styled(TablePageCard)`
  min-width: 0;
  overflow: auto;
`;

export const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxl)};
`;

export const TitleBlock = styled.div`
  min-width: min(${pxToRem(280)}, 100%);
`;

export const Title = styled(TablePageTitle)``;

export const Subtitle = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.xs)} 0 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(Math.max(theme.fontSize.md, 16))};
  line-height: 1.55;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};

  @media (max-width: 720px) {
    width: 100%;
  }
`;

export const ActionButton = styled.button<{ $primary?: boolean }>`
  min-height: ${({ theme }) => pxToRem(Math.max(theme.controlHeight.md, 48))};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: 1px solid ${({ theme, $primary }) => $primary ? theme.colors.brand : theme.colors.brandMedium};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme, $primary }) => $primary ? theme.colors.brand : theme.colors.white};
  color: ${({ theme, $primary }) => $primary ? theme.colors.white : theme.colors.brand};
  font: inherit;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  cursor: pointer;
  white-space: normal;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (max-width: 720px) {
    flex: 1 1 ${pxToRem(190)};
  }
`;

export const UsageCard = styled.section`
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: 1px solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const UsageTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const UsagePercent = styled.strong`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xxl)};
`;

export const UsageAmount = styled.span`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
`;

export const Track = styled.div`
  height: ${pxToRem(12)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.md)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.inkSurface};
`;

const progressColor = (color: "green" | "yellow" | "red") => {
  if (color === "red") return "#e52c6d";
  if (color === "yellow") return "#ffc107";
  return "#56bd74";
};

export const Progress = styled.div<{ $percent: number; $color: "green" | "yellow" | "red" }>`
  width: ${({ $percent }) => `${Math.min(100, Math.max(0, $percent))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ $color }) => progressColor($color)};
  transition: width 180ms ease;
`;

export const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(${pxToRem(210)}, 100%), 1fr));
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const CategoryCard = styled.article`
  min-width: 0;
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: 1px solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.surfaceCool};
`;

export const CategoryName = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
`;

export const CategoryValue = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.sm)} 0 0;
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 700;
`;

export const Note = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.xl)} 0 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.55;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  background: ${({ theme }) => theme.colors.shadowStrong};
`;

export const Modal = styled.div`
  width: min(${pxToRem(460)}, 100%);
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
`;

export const CodeInput = styled.input`
  width: 100%;
  min-height: ${pxToRem(52)};
  box-sizing: border-box;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)};
  border: 1px solid ${({ theme }) => theme.borders.field};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${pxToRem(18)};
  letter-spacing: 0.3em;
  text-align: center;
`;

export const RequestFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(${pxToRem(110)}, ${pxToRem(150)});
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};

  @media (max-width: 440px) {
    grid-template-columns: 1fr;
  }
`;

export const RequestInput = styled.input`
  min-width: 0;
  min-height: ${({ theme }) => pxToRem(theme.controlHeight.md)};
  box-sizing: border-box;
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)};
  border: 1px solid ${({ theme }) => theme.borders.field};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
`;

export const RequestSelect = styled.select`
  min-width: 0;
  min-height: ${({ theme }) => pxToRem(theme.controlHeight.md)};
  box-sizing: border-box;
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)};
  border: 1px solid ${({ theme }) => theme.borders.field};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
`;

export const ModalActions = styled(Actions)`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
`;

export const ErrorText = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.sm)} 0 0;
  color: ${({ theme }) => theme.colors.dangerText};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const FeatureCard = styled.section`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)};
  border: 1px solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const FeatureTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(${pxToRem(220)}, 1fr) minmax(${pxToRem(240)}, 1fr) auto;
  align-items: end;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldLabel = styled.label`
  display: grid;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  min-width: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 600;
`;

export const TextInput = styled(RequestInput)``;
export const SelectInput = styled(RequestSelect)`width: 100%;`;
